import React from 'react';

import { isSet, Unset } from '@utils';

export interface Widget<T = any, K = T> {
  value: T;
  onValueChange: (v: K) => void;
  onBlur?: () => void;
}

type WithWidgetRequiredFields<T extends (...args: object[]) => void> =
  Parameters<T>[0] extends Widget
    ? T
    : 'Widget has to contain value and onValueChange props';

type MakeOptional<T extends object, F extends keyof T> = Omit<T, F> &
  Partial<Pick<T, F>>;

export type EditorFieldWidgetProps<
  T extends (...params: any) => JSX.Element,
  K = Parameters<T>[0]
> = {
  widget: WithWidgetRequiredFields<T>;
  // TODO should be optional when has more params than value and onValueChange
  widgetProps?: Parameters<T>[0] extends Widget
    ? MakeOptional<Parameters<T>[0], 'value' | 'onValueChange'>
    : never;
};

export interface InlineEditorProps<T = any> {
  value: T | Unset;
  onValueChange: (v: T) => void;
  label: string;
  prettifyFn?: (value: T) => string;
  valueType?: 'number' | 'string';
}

export function InlineEditor<T extends (...params: any) => JSX.Element>(
  props: InlineEditorProps & EditorFieldWidgetProps<T>
) {
  const value = props.value ?? null;
  const onValueChange = props.onValueChange;

  const [isSummaryVisible, setIsSummaryVisible] = React.useState(() => true);

  const Widget = () =>
    typeof props.widget === 'function'
      ? props.widget({
          ...props.widgetProps,
          value,
          onValueChange,
          onBlur: () => setIsSummaryVisible(true),
        })
      : null;
  const displayedValue =
    props.prettifyFn && isSet(value)
      ? props.prettifyFn(value)
      : `'${value ?? '...'}'`;

  const valueTypeClass = `${props.valueType ?? 'string'}-value`;

  return (
    <span className="inline-editor">
      {props.label}
      <span className="equal-sign">=</span>
      {isSummaryVisible ? (
        <span
          className={`inline-displayed-value ${valueTypeClass}`}
          onClick={() => setIsSummaryVisible(false)}
        >
          {displayedValue}
        </span>
      ) : (
        <span style={{ marginTop: '6px', marginLeft: '1px' }}>{Widget()} </span>
      )}
    </span>
  );
}
